<!--
 * @Author: LiZhiWei
 * @Date: 2026-08-12 10:00:00
 * @Description: 全局 Toast 容器，基于 reka-ui Toast 原语，仅在客户端渲染
-->
<template>
  <ToastProvider :duration="3000" label="通知">
    <ToastRoot
      v-for="t in toasts"
      :key="t.id"
      :duration="t.duration"
      :type="t.variant === 'error' || t.variant === 'warning' ? 'foreground' : 'background'"
      :open="true"
      @update:open="(v) => !v && dismiss(t.id)"
    >
      <div class="toast" :data-variant="t.variant">
        <i class="toast-icon" :class="toastIcons[t.variant]" aria-hidden="true"></i>
        <span class="sr-only">{{ toastLabels[t.variant] }}</span>
        <div class="toast-body">
          <ToastTitle v-if="t.title" class="toast-title">{{ t.title }}</ToastTitle>
          <ToastDescription v-if="t.description" class="toast-desc">
            {{ t.description }}
          </ToastDescription>
        </div>
        <ToastClose class="toast-close" aria-label="关闭通知">✕</ToastClose>
      </div>
    </ToastRoot>
    <ToastViewport class="toast-viewport" />
  </ToastProvider>
</template>

<script setup lang="ts">
  import type { ToastVariant } from '~/composables/useToast'

  import {
    ToastClose,
    ToastDescription,
    ToastProvider,
    ToastRoot,
    ToastTitle,
    ToastViewport,
  } from 'reka-ui'

  import { useToast } from '~/composables/useToast'

  const { toasts, dismiss } = useToast()
  const toastIcons: Record<ToastVariant, string> = {
    error: 'i-lucide-circle-x',
    info: 'i-lucide-info',
    success: 'i-lucide-circle-check',
    warning: 'i-lucide-triangle-alert',
  }
  const toastLabels: Record<ToastVariant, string> = {
    error: '错误',
    info: '提示',
    success: '成功',
    warning: '警告',
  }
</script>

<style scoped lang="scss">
  :global(.toast-viewport) {
    position: fixed;
    top: 16px;
    right: 16px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: min(360px, calc(100vw - 32px));
    padding: 0;
    margin: 0;
    list-style: none;
  }

  .toast {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 14px 16px;
    color: var(--ink);
    background: var(--surface);
    border-radius: 10px;
    box-shadow: 0 12px 32px rgb(0 0 0 / 12%);
    animation: toast-in 180ms ease;
  }

  .toast[data-variant='success'] {
    --toast-accent: #16a34a;
  }

  .toast[data-variant='error'] {
    --toast-accent: var(--brand);
  }

  .toast[data-variant='warning'] {
    --toast-accent: #d97706;
  }

  .toast[data-variant='info'] {
    --toast-accent: #2563eb;
  }

  .toast-icon {
    flex: 0 0 auto;
    width: 16px;
    height: 16px;
    margin-top: 1px;
    color: var(--toast-accent);
  }

  .toast-body {
    flex: 1;
    min-width: 0;
  }

  .toast-title {
    display: block;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
  }

  .toast-desc {
    display: block;
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: var(--ink-muted);
  }

  .toast-title + .toast-desc {
    margin-top: 4px;
  }

  .toast-close {
    flex: 0 0 auto;
    font-size: 14px;
    line-height: 1;
    color: var(--ink-muted);
    cursor: pointer;
    background: transparent;
    border: 0;
  }

  @media (hover: hover) and (pointer: fine) {
    .toast-close:hover {
      color: var(--ink);
    }
  }

  @media (hover: none) {
    .toast-close:active {
      color: var(--ink);
    }
  }

  @keyframes toast-in {
    from {
      opacity: 0;
      transform: translateX(16px);
    }

    to {
      opacity: 1;
      transform: none;
    }
  }
</style>
